"use client";

import { useCallback, useEffect, useRef, useState } from "react";

/**
 * The transaction flow's non-visual half — Phase 06.
 *
 * Two things every money path needs and none of them had:
 *
 *  1. A SUBMISSION GUARD that survives a double click. `disabled={submitting}`
 *     is not one: `setState` is asynchronous, so two clicks inside the same
 *     React batch both observe `submitting === false` and both fire. The guard
 *     here is a ref, written synchronously before the request leaves.
 *
 *  2. A DURABLE RESULT. A sonner toast is a transient acknowledgement on a
 *     timer; it is the wrong and only record of a listing, an offer or an
 *     accepted sale. `useTransactionLedger` keeps every completed attempt on
 *     the page, readable after the toast is gone and after the dialog is
 *     closed, and mirrors it into `sessionStorage` so a reload — including the
 *     `router.refresh()` these flows trigger — does not erase it.
 *
 * IDEMPOTENCY IS NOT CLAIMED. Each attempt carries a client-generated
 * `requestId`. It is a CLIENT-side de-duplication key and an identifier the
 * user can quote to support; it is deliberately NOT transmitted, because no
 * endpoint in this API accepts or documents one, and sending a header the
 * backend does not store would let the surface imply a guarantee that does not
 * exist (Q8 unresolved). The guarantee this file makes is exactly: one
 * confirmed click produces at most one in-flight request from this client.
 *
 * The ledger is a client-side record of what THIS browser did, not a receipt
 * store — Q18 (durable receipts) is still open, and nothing here pre-empts it.
 * It displays what the server actually returned and never invents an id.
 */

export type TransactionRef = {
  label: string;
  value: string;
  /** Renders mono + LTR-isolated. For hashes, addresses and ids. */
  technical?: boolean;
};

export type TransactionRecord = {
  /** Client-generated per attempt. Not sent to the backend — see the note above. */
  requestId: string;
  /** Which money path produced this, e.g. "list-for-sale". */
  flow: string;
  outcome: "success" | "failure";
  title: string;
  description?: string;
  /** Only what the server actually returned. Empty when it returned nothing. */
  refs?: TransactionRef[];
  /** ISO timestamp, taken at completion. */
  at: string;
};

export function createRequestId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // Older Safari and any non-secure context. The id only has to be unique
  // within one browser session, so this fallback is sufficient.
  return `req-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
}

/**
 * Runs one async action at a time. A call made while another is in flight is
 * DROPPED, not queued — a queued second submit is still a second transaction,
 * which is the outcome this exists to prevent.
 *
 * Returns `{ skipped: true }` for the dropped call so a caller can tell the
 * difference between "did nothing" and "succeeded".
 */
export type SingleFlightOutcome<T> =
  | { skipped: true }
  | { skipped: false; requestId: string; result: T };

/**
 * The hook's whole rule, as a plain function over a mutable gate — so the
 * "a second click sends nothing" invariant is testable without a renderer.
 * The gate is shaped like a `useRef` cell precisely so the hook can pass its
 * own ref straight through.
 */
export async function runSingleFlight<T>(
  gate: { current: boolean },
  action: (requestId: string) => Promise<T>,
  onPendingChange?: (pending: boolean) => void,
): Promise<SingleFlightOutcome<T>> {
  // Synchronous, before any await: two clicks in one React batch both
  // reach this line, and only the first sees `false`.
  if (gate.current) return { skipped: true };
  gate.current = true;
  onPendingChange?.(true);

  const requestId = createRequestId();
  try {
    const result = await action(requestId);
    return { skipped: false, requestId, result };
  } finally {
    gate.current = false;
    onPendingChange?.(false);
  }
}

export function useSingleFlight() {
  const inFlight = useRef(false);
  const mounted = useRef(true);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const run = useCallback(
    <T,>(action: (requestId: string) => Promise<T>): Promise<SingleFlightOutcome<T>> =>
      runSingleFlight(inFlight, action, (next) => {
        if (mounted.current) setPending(next);
      }),
    [],
  );

  return { run, pending };
}

const LEDGER_LIMIT = 5;

/** Pure — the reducer is unit-testable without a DOM. */
export function appendRecord(
  records: TransactionRecord[],
  record: TransactionRecord,
  limit = LEDGER_LIMIT,
): TransactionRecord[] {
  const withoutDuplicate = records.filter((r) => r.requestId !== record.requestId);
  return [record, ...withoutDuplicate].slice(0, limit);
}

/** Pure. Tolerates anything in storage — a corrupt entry drops the whole read. */
export function parseLedger(raw: string | null): TransactionRecord[] {
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (r): r is TransactionRecord =>
        typeof r === "object" &&
        r !== null &&
        typeof (r as TransactionRecord).requestId === "string" &&
        typeof (r as TransactionRecord).title === "string" &&
        ((r as TransactionRecord).outcome === "success" || (r as TransactionRecord).outcome === "failure"),
    );
  } catch {
    return [];
  }
}

/**
 * The durable result store for one subject (here: one NFT).
 *
 * `sessionStorage`, not `localStorage`: this is the record of what happened in
 * this session, and it must not resurface months later on a shared machine as
 * if it were current. Reading happens in an effect so server and first client
 * render agree.
 */
export function useTransactionLedger(storageKey: string, limit = LEDGER_LIMIT) {
  const [records, setRecords] = useState<TransactionRecord[]>([]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      setRecords(parseLedger(window.sessionStorage.getItem(storageKey)));
    } catch {
      // Storage disabled (private mode, blocked cookies). The in-memory
      // record still works for the life of the page — degraded, not broken.
      setRecords([]);
    }
  }, [storageKey]);

  // Functional updates throughout: two results can land in the same tick (a
  // retry after a failure), and a stale closure over `records` would drop one.
  const persist = useCallback(
    (update: (previous: TransactionRecord[]) => TransactionRecord[]) => {
      setRecords((previous) => {
        const next = update(previous);
        if (typeof window !== "undefined") {
          try {
            window.sessionStorage.setItem(storageKey, JSON.stringify(next));
          } catch {
            // Ignored on purpose: failing to persist must not fail the flow.
          }
        }
        return next;
      });
    },
    [storageKey],
  );

  const record = useCallback(
    (entry: TransactionRecord) => {
      persist((previous) => appendRecord(previous, entry, limit));
    },
    [persist, limit],
  );

  const dismiss = useCallback(
    (requestId: string) => {
      persist((previous) => previous.filter((r) => r.requestId !== requestId));
    },
    [persist],
  );

  return { records, record, dismiss };
}
